import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Pulse,
  ShieldCheck,
  Heart,
  Airplane,
  ShareNetwork,
  GoogleLogo,
  LockKey,
  Warning,
  Copy,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { isInAppBrowser } from "@/lib/inAppBrowser";
import ContactNote from "@/components/ContactNote";

const FEATURES = [
  {
    icon: Heart,
    title: "Botón gigante",
    desc: "Un toque cada 48 horas confirma que estás bien.",
  },
  {
    icon: Warning,
    title: "Alerta automática",
    desc: "Si olvidas el check-in, tus contactos reciben un email.",
  },
  {
    icon: Airplane,
    title: "Modo vacaciones",
    desc: "Pausa el temporizador cuando viajes. Sin falsas alarmas.",
  },
  {
    icon: LockKey,
    title: "PIN de seguridad",
    desc: "Evita activaciones accidentales con PIN configurable.",
  },
  {
    icon: ShareNetwork,
    title: "Enlace público",
    desc: "Comparte un link de solo-lectura con tu familia.",
  },
  {
    icon: ShieldCheck,
    title: "Privacidad",
    desc: "Tus contactos y mensajes nunca se exponen públicamente.",
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [signing, setSigning] = useState(false);
  const [inApp, setInApp] = useState(false);
  const authError = searchParams.get("auth_error");

  useEffect(() => {
    setInApp(isInAppBrowser());
  }, []);

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);

  const handleGoogleSignIn = () => {
    if (inApp) {
      toast.error("Abre AquíSigo en Safari o Chrome", {
        description: "Google bloquea el login desde apps embebidas.",
      });
      return;
    }
    setSigning(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  const copyCurrentUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="min-h-screen w-full text-white" data-testid="landing-page">
      <header className="w-full border-b border-border/60">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Pulse size={26} weight="duotone" className="text-aqua" />
            <span className="font-heading font-black text-xl tracking-[0.4em]">
              AQUÍSIGO
            </span>
          </div>
          <Button
            onClick={handleGoogleSignIn}
            disabled={signing}
            className="bg-aqua text-background hover:bg-aqua/90"
            data-testid="header-signin-btn"
          >
            <GoogleLogo size={16} weight="bold" className="mr-2" />
            Iniciar sesión
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {inApp && (
          <div
            className="mb-10 p-5 rounded-2xl bg-warn/10 border border-warn/40"
            data-testid="inapp-warning-banner"
          >
            <div className="flex items-start gap-3">
              <Warning size={22} weight="duotone" className="text-warn shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold text-warn mb-1">
                  Estás en un navegador embebido
                </div>
                <p className="text-sm text-white/75 leading-relaxed mb-3">
                  Google bloquea el inicio de sesión desde apps como Instagram, Facebook,
                  Twitter o LinkedIn (error <span className="mono text-warn">disallowed_useragent</span>).
                  Abre AquíSigo directamente en <strong>Safari, Chrome, Firefox o Edge</strong> para iniciar sesión.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    onClick={copyCurrentUrl}
                    size="sm"
                    variant="outline"
                    className="border-warn/50 text-warn hover:bg-warn/10 hover:text-warn"
                    data-testid="inapp-copy-btn"
                  >
                    <Copy size={14} weight="bold" className="mr-1.5" />
                    Copiar enlace
                  </Button>
                  <a
                    href={typeof window !== "undefined" ? window.location.origin : "/"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-warn hover:underline"
                    data-testid="inapp-open-external"
                  >
                    <ArrowSquareOut size={14} weight="bold" />
                    Abrir en navegador externo
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-24">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.4em] text-white/50 mb-4">
              Dead-man's switch personal · Cada 48h
            </p>
            <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] mb-6">
              Si algo te pasa,
              <br />
              <span className="text-aqua">ellos lo sabrán.</span>
            </h1>
            <p className="text-base md:text-lg text-white/70 max-w-xl mb-8">
              AquíSigo es un sistema de seguridad personal silencioso. Presionas un botón
              cada 48 horas para indicar que estás bien. Si no lo haces, enviamos un
              email automático a tus contactos de emergencia avisándoles que algo te
              pudo haber pasado.
            </p>

            {authError && (
              <div
                className="mb-5 px-4 py-3 rounded-lg bg-danger/10 border border-danger/40 text-danger text-sm"
                data-testid="auth-error-banner"
              >
                Hubo un problema al iniciar sesión. Inténtalo de nuevo.
              </div>
            )}

            <Button
              onClick={handleGoogleSignIn}
              disabled={signing}
              size="lg"
              className="bg-aqua text-background hover:bg-aqua/90 text-base font-semibold px-7 py-6 h-auto"
              data-testid="hero-signin-btn"
            >
              <GoogleLogo size={20} weight="bold" className="mr-2.5" />
              {signing ? "Redirigiendo..." : "Iniciar sesión con Google"}
            </Button>
            <p className="mt-3 text-xs text-white/40">
              Tu cuenta y tus contactos quedan privados. No compartimos datos.
            </p>
          </div>

          {/* Visual block */}
          <div className="lg:col-span-5">
            <div className="relative aspect-square max-w-[420px] mx-auto">
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-aqua/30"
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 3.4, ease: "easeInOut", repeat: Infinity }}
              />
              <motion.div
                className="absolute inset-8 rounded-full border border-aqua/40"
                animate={{ scale: [1, 1.04, 1] }}
                transition={{ duration: 3.4, ease: "easeInOut", repeat: Infinity, delay: 0.4 }}
              />
              <div
                className="absolute inset-16 rounded-full flex items-center justify-center"
                style={{
                  background:
                    "radial-gradient(circle at 50% 35%, #1A1A20 0%, #121216 55%, #0A0A0E 100%)",
                  border: "2px solid #00E5FF",
                  boxShadow: "0 0 60px rgba(0,229,255,0.4)",
                }}
              >
                <Pulse size={88} weight="duotone" className="text-aqua" />
              </div>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 mono font-mono text-aqua/80 text-xs uppercase tracking-[0.4em]">
                47:59:59
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-24">
          <p className="text-xs uppercase tracking-[0.4em] text-white/50 mb-3 text-center">
            Cómo funciona
          </p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-center mb-12 max-w-3xl mx-auto leading-tight">
            Una herramienta silenciosa para quienes
            <br />
            <span className="text-aqua">se cuidan solos.</span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="bg-surface border border-border rounded-2xl p-6 hover:border-aqua/40 transition-colors"
                  data-testid={`feature-card-${i}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-aqua/10 border border-aqua/30 flex items-center justify-center text-aqua mb-4">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <h3 className="font-heading font-bold text-lg tracking-tight mb-1.5">
                    {f.title}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-surface border border-border rounded-3xl p-10 md:p-16">
          <h3 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-3 max-w-2xl mx-auto">
            Tu pulso, su tranquilidad.
          </h3>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Crea tu cuenta en segundos con Google y configura tu primer check-in hoy mismo.
          </p>
          <Button
            onClick={handleGoogleSignIn}
            disabled={signing}
            size="lg"
            className="bg-aqua text-background hover:bg-aqua/90 text-base font-semibold px-7 py-6 h-auto"
            data-testid="cta-signin-btn"
          >
            <GoogleLogo size={20} weight="bold" className="mr-2.5" />
            Iniciar sesión con Google
          </Button>
        </section>

        <ContactNote className="mt-16" />

        <footer className="mt-6 pt-6 border-t border-border/60 text-xs text-white/40 flex flex-wrap items-center justify-between gap-2">
          <span>AquíSigo · Sistema de seguridad personal</span>
          <span className="mono">v2.0</span>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;
